import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star, X, Crosshair, Shield, Zap, Lock, Swords, Info, Check } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FACTION = {
  Void: { color: "#a78bfa", glow: "rgba(167,139,250,0.55)" },
  Bio: { color: "#37e0a0", glow: "rgba(55,224,160,0.55)" },
  Plasma: { color: "#00d5ff", glow: "rgba(0,213,255,0.55)" },
  Mech: { color: "#4aa3ff", glow: "rgba(74,163,255,0.55)" },
  Recon: { color: "#38bdf8", glow: "rgba(56,189,248,0.55)" },
  Tech: { color: "#22d3ee", glow: "rgba(34,211,238,0.55)" },
  Shadow: { color: "#8b5cf6", glow: "rgba(139,92,246,0.55)" },
  Cosmic: { color: "#f5c542", glow: "rgba(245,197,66,0.6)" },
  Cyber: { color: "#c084fc", glow: "rgba(192,132,252,0.55)" },
};
const TIER_COLOR = { S: "#f5c542", A: "#c084fc", R: "#4aa3ff" };

const img = (path) => (path ? (path.startsWith("http") ? path : BACKEND + path) : null);
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}K` : `${n}`);

const Stars = ({ n }) => (
  <span className="hr-stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={11} fill={i < n ? "#f5c542" : "none"} color={i < n ? "#f5c542" : "#4a5568"} />
    ))}
  </span>
);

// SquadRoster — 9:16 portrait card deck. Tap a card to pick your active squad
// (any 2 of the 8-hero pool); the info button opens the full stat + skill matrix.
export default function SquadRoster({ onBack }) {
  const [heroes, setHeroes] = useState(null);
  const [deployed, setDeployed] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    spaceApi.heroesRoster().then((d) => {
      setHeroes(d.heroes || []);
      setDeployed(d.deployed || []);
      setSelected(d.deployed || []);
    }).catch(() => setHeroes([]));
  }, []);

  const toggleSelect = (cid) => {
    setSelected((cur) => {
      if (cur.includes(cid)) return cur.filter((x) => x !== cid);
      if (cur.length >= 2) { toast.warning("Squad is full — deselect a hero first"); return cur; }
      return [...cur, cid];
    });
  };

  const deploy = async () => {
    if (selected.length !== 2 || busy) return;
    setBusy(true);
    try {
      const r = await spaceApi.heroesDeploy(selected);
      setDeployed(r.deployed_squad || selected);
      toast.success("Squad deployed to the arena");
    } catch (e) { toast.error(e.message || "Deploy failed"); }
    finally { setBusy(false); }
  };

  const dirty = selected.length === 2 && (selected.join() !== deployed.join());

  return (
    <div className="ss-viewport" data-testid="space-supremacy-app">
      <div className="ss-atmosphere" />
      <div className="ss-grid" />
      <SpaceHomeBtn onClick={onBack} testid="squad-roster-back" />

      <div className="hr-wrap" data-testid="squad-roster">
        <div className="hr-head">
          <span className="hr-eyebrow">Mist Play · Command Squad</span>
          <h1>Squad Roster</h1>
        </div>

        <div className="hr-deploybar" data-testid="squad-deploy-bar">
          <span className="hr-deploy-count">Active Squad · <b>{selected.length}</b>/2</span>
          <button className="hr-deploy-btn" disabled={!dirty || busy} onClick={deploy} data-testid="squad-deploy-btn">
            {busy ? "Deploying…" : selected.length === 2 ? (dirty ? "Deploy Squad" : "Deployed") : "Pick 2 heroes"}
          </button>
        </div>

        {heroes === null ? (
          <div className="bg-loading" data-testid="squad-roster-loading"><Loader2 size={26} className="bg-spin" /><span>Syncing profiles…</span></div>
        ) : (
          <div className="hr-grid" data-testid="squad-roster-grid">
            {heroes.map((h) => {
              const fac = FACTION[h.faction] || FACTION.Plasma;
              const isSel = selected.includes(h.card_id);
              const isDep = deployed.includes(h.card_id);
              return (
                <div key={h.card_id} className={`hr-card ${isSel ? "sel" : ""}`} role="button" tabIndex={0}
                  data-testid={`hero-card-${h.card_id}`} style={{ "--fac": fac.color, "--facglow": fac.glow }}
                  onClick={() => toggleSelect(h.card_id)}>
                  <div className="hr-portrait">
                    {img(h.image_path)
                      ? <img src={img(h.image_path)} alt={h.name} data-testid={`hero-portrait-${h.card_id}`} />
                      : <div className="hr-noimg">NO SIGNAL</div>}
                    <span className="hr-tier" style={{ background: TIER_COLOR[h.rarity_tier] }} data-testid={`hero-tier-${h.card_id}`}>{h.rarity_tier}</span>
                    <span className="hr-faction" data-testid={`hero-faction-${h.card_id}`}>{h.faction}</span>
                    <span className="hr-lv" data-testid={`hero-level-${h.card_id}`}>Lv {h.current_level}</span>
                    <button className="hr-info" onClick={(e) => { e.stopPropagation(); setSel(h); }} data-testid={`hero-info-${h.card_id}`} aria-label="Hero details"><Info size={14} /></button>
                    {isDep && <span className="hr-deployed" data-testid={`hero-deployed-${h.card_id}`}>DEPLOYED</span>}
                    {isSel && <span className="hr-selcheck" data-testid={`hero-selected-${h.card_id}`}><Check size={14} /></span>}
                  </div>
                  <div className="hr-info-body">
                    <span className="hr-name">{h.name}</span>
                    <span className="hr-title">{h.title}</span>
                    {h.weapon_trait && (
                      <span className="hr-trait" data-testid={`hero-trait-${h.card_id}`}><Swords size={9} /> {h.weapon_trait}</span>
                    )}
                    <div className="hr-row">
                      <Stars n={h.combat_stars} />
                      <span className="hr-power" data-testid={`hero-power-${h.card_id}`}><Zap size={10} /> {fmt(h.power_score)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sel && <HeroDetail hero={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

const HeroDetail = ({ hero, onClose }) => {
  const fac = FACTION[hero.faction] || FACTION.Plasma;
  const bs = hero.base_stats;
  return (
    <div className="hr-modal" onClick={onClose} data-testid="hero-detail">
      <div className="hr-modal-box" onClick={(e) => e.stopPropagation()} style={{ "--fac": fac.color, "--facglow": fac.glow }}>
        <button className="hr-modal-x" onClick={onClose} data-testid="hero-detail-close"><X size={18} /></button>
        <div className="hr-detail-hero">
          <div className="hr-detail-portrait">
            {img(hero.image_path) ? <img src={img(hero.image_path)} alt={hero.name} /> : <div className="hr-noimg">NO SIGNAL</div>}
          </div>
          <div className="hr-detail-id">
            <span className="hr-tier lg" style={{ background: TIER_COLOR[hero.rarity_tier] }}>{hero.rarity_tier}</span>
            <span className="hr-detail-name">{hero.name}</span>
            <span className="hr-detail-title">{hero.title}</span>
            <span className="hr-detail-faction">{hero.faction} · Lv {hero.current_level}/{hero.max_level}</span>
            <Stars n={hero.combat_stars} />
          </div>
        </div>

        <div className="hr-stats" data-testid="hero-detail-stats">
          <div className="hr-stat"><Crosshair size={13} /><span className="hr-stat-l">Firepower</span><b>{bs.firepower.toLocaleString()}</b></div>
          <div className="hr-stat"><Shield size={13} /><span className="hr-stat-l">Defense</span><b>{bs.defense_rating.toLocaleString()}</b></div>
          <div className="hr-stat"><Zap size={13} /><span className="hr-stat-l">Shield</span><b>{bs.tactical_shield.toLocaleString()}</b></div>
        </div>

        <div className="hr-skills-t">Skill Matrix</div>
        <div className="hr-skills" data-testid="hero-detail-skills">
          {hero.special_skills.map((s) => {
            const locked = s.type === "Locked Ultimate";
            return (
              <div key={s.slot} className={`hr-skill ${locked ? "locked" : ""}`} data-testid={`hero-skill-${hero.card_id}-${s.slot}`}>
                <div className="hr-skill-h">
                  <span className="hr-skill-slot">{locked ? <Lock size={11} /> : s.slot}</span>
                  <span className="hr-skill-name">{s.skill_name}</span>
                  <span className="hr-skill-type">{s.type}</span>
                  {!locked && <span className="hr-skill-lv">Lv {s.level}</span>}
                </div>
                <p className="hr-skill-desc">{s.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
